import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { Form } from 'react-bootstrap';
import { Property } from '../properties';
import { EditorContext } from '../../index';
import { updateWidgetProperty } from 'actions/editor';
import cx from 'classnames';
import { getParentContainerOfType } from 'components/Editor/helper/widget';
import { CAROUSEL } from 'constants/widgetTypes';
import Tooltip from 'components/Tooltip';
import styles from './autoGrow.module.scss';

export const AutoGrowProperty = ({ widget, onCancel }) => {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  
  const id = _.get(widget, 'uid');
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const autoGrow = _.get(widget, `properties.${breakpoint}.autoGrow`, false);
  const isInsideCarousel = !!getParentContainerOfType(editorSession, id, CAROUSEL);

  const handleToggle = (e) => {
    if (isInsideCarousel) return;
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, {
      autoGrow: e.target.checked
    }));
  };

  return (
    <Property.Dialog 
      className={styles.autoGrowDialog}
      width={300}
      target={`#${id}`}
      title={'Auto Grow'}
      onCancel={onCancel}
    >
      <Property.Body>
        <div className={styles.container}>
          <div className={styles.message}>
            <p className={styles.description}>
              Activate this to automatically expand the HTML widget to fit its content and push widgets below accordingly.
            </p>
            <div className={styles.toggleRow}>
              <span className={styles.label}>Enable Auto Grow</span>
              <Tooltip
                placement="top"
                content="Auto Grow isn't supported for HTML widgets inside a carousel"
                disabled={!isInsideCarousel}
              >
                <span className={cx(styles.toggleWrapper, { [styles.disabled]: isInsideCarousel })}>
                  <Form.Check 
                    type="switch"
                    id="auto-grow-switch"
                    className={styles.toggleSwitch}
                    checked={autoGrow}
                    disabled={isInsideCarousel}
                    size={"sm"}
                    onChange={handleToggle}
                  />
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </Property.Body>
    </Property.Dialog>
  );
};


/**
 * Recursively disables autoGrow in a widget and all its nested children.
 * Used when Live Elements (with nested HTML that may have autoGrow) are moved into a carousel.
 */
export const disableAutoGrowInWidgetTree = (widget) => {
  if (!widget) return widget;
  const hasAutoGrow = _.get(widget, 'properties.desktop.autoGrow') || _.get(widget, 'properties.mobile.autoGrow');
  let updatedWidget = widget;
  if (hasAutoGrow) {
    updatedWidget = {
      ...widget,
      properties: {
        ...widget.properties,
        desktop: { ...(widget.properties?.desktop || {}), autoGrow: false },
        mobile: { ...(widget.properties?.mobile || {}), autoGrow: false }
      }
    };
  }
  if (_.isArray(updatedWidget.children) && updatedWidget.children.length > 0) {
    updatedWidget = {
      ...updatedWidget,
      children: updatedWidget.children.map(child => disableAutoGrowInWidgetTree(child))
    };
  }
  return updatedWidget;
}

export const getDescendantIds = (widgets, rootId) => {
  const ids = new Set();
  let toProcess = [rootId];
  while (toProcess.length > 0) {
    const currentId = toProcess.pop();
    widgets.forEach((w) => {
      if (w.parent === currentId && !ids.has(w.uid)) {
        ids.add(w.uid);
        toProcess.push(w.uid);
      }
    });
  }
  return ids;
}
export function hasAutoGrowInWidgetTree(widget) {
  if (!widget) return false;
  if (_.get(widget, 'properties.desktop.autoGrow') || _.get(widget, 'properties.mobile.autoGrow')) return true;
  return _.isArray(widget.children) && widget.children.some(child => hasAutoGrowInWidgetTree(child));
}
export function hadAutoGrowInWidgetOrDescendants(widgets, widgetId) {
  const widget = widgets.find(w => w.uid === widgetId);
  if (!widget) return false;
  if (hasAutoGrowInWidgetTree(widget)) return true;
  const descendantIds = getDescendantIds(widgets, widgetId);
  return [...descendantIds].some(id => {
    const w = widgets.find(x => x.uid === id);
    return w && hasAutoGrowInWidgetTree(w);
  });
}